<template>
  <div class="min-h-screen flex-1 mt-1 bg-gray-100 px-4 pt-16 shadow-lg items-center">
    <div class="bg-white w-full rounded-lg shadow">
      <!-- Header -->
      <div class="h-20 py-3 flex items-center border-gray-200 p-6 mt-5">
        <div class="flex">
          <button
            :class="$colors.primary"
            class="rounded-full flex justify-center items-center w-8 h-8 mr-3"
            @click="$router.back()"
          >
            <i class="material-icons">chevron_left</i>
          </button>
          <div class="text-2xl font-bold text-green-500">
            {{ getCompanyName() || 'Détails Partenaire' }}
          </div>
        </div>
        <div class="ml-auto flex space-x-2">
          <router-link
            :to="`/partners/edit/${$route.params.id}`"
            class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center"
          >
            <i class="material-icons text-sm mr-1">edit</i>
            Modifier
          </router-link>
        </div>
      </div>

      <div class="relative">
        <loading :active.sync="loading" :is-full-page="false"></loading>

        <div v-if="partner" class="p-6 space-y-6">
          <!-- Overview Cards -->
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div class="bg-green-50 rounded-lg p-4">
              <div class="text-sm text-green-600 font-medium">Total Commandes</div>
              <div class="text-2xl font-bold text-green-700">{{ getTotalOrders() }}</div>
            </div>
            <div class="bg-blue-50 rounded-lg p-4">
              <div class="text-sm text-blue-600 font-medium">Total Requêtes API</div>
              <div class="text-2xl font-bold text-blue-700">{{ getTotalRequests() }}</div>
            </div>
            <div class="bg-purple-50 rounded-lg p-4">
              <div class="text-sm text-purple-600 font-medium">Type de Contrat</div>
              <div class="text-2xl font-bold text-purple-700 capitalize">{{ getContractType() }}</div>
            </div>
            <div class="bg-yellow-50 rounded-lg p-4">
              <div class="text-sm text-yellow-600 font-medium">Statut</div>
              <div class="text-2xl font-bold capitalize" :class="getStatusColor(partner.status)">{{ partner.status }}</div>
            </div>
          </div>

          <!-- Information Sections -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Basic Info -->
            <div class="border rounded-lg p-4">
              <h3 class="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <i class="material-icons text-sm mr-2">business</i>
                Informations de base
              </h3>
              <div class="space-y-3">
                <div class="flex justify-between">
                  <span class="text-gray-500">Entreprise</span>
                  <span class="font-medium">{{ getCompanyName() || '-' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Nom complet</span>
                  <span class="font-medium">{{ partner.fullName || '-' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Email</span>
                  <span class="font-medium">{{ partner.email }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Téléphone</span>
                  <span class="font-medium">{{ partner.phone }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Pays autorisés</span>
                  <span class="font-medium">{{ (partner.countries || []).join(', ') || '-' }}</span>
                </div>
              </div>
            </div>

            <!-- Contract Info -->
            <div class="border rounded-lg p-4">
              <h3 class="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <i class="material-icons text-sm mr-2">description</i>
                Informations du contrat
              </h3>
              <div class="space-y-3">
                <div class="flex justify-between">
                  <span class="text-gray-500">Type de contrat</span>
                  <span class="font-medium capitalize">{{ getContractType() }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Date de début</span>
                  <span class="font-medium">{{ formatDate(getContractStartDate()) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Date de fin</span>
                  <span class="font-medium">{{ formatDate(getContractEndDate()) || 'Illimité' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Actif</span>
                  <span :class="getIsActive() ? 'text-green-600' : 'text-red-600'" class="font-medium">
                    {{ getIsActive() ? 'Oui' : 'Non' }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Créé le</span>
                  <span class="font-medium">{{ formatDate(partner.createdAt) }}</span>
                </div>
              </div>
            </div>

            <!-- Rate Limits -->
            <div class="border rounded-lg p-4">
              <h3 class="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <i class="material-icons text-sm mr-2">speed</i>
                Limitations de débit
              </h3>
              <div class="space-y-3">
                <div class="flex justify-between">
                  <span class="text-gray-500">Requêtes/minute</span>
                  <span class="font-medium">{{ getRequestsPerMinute() }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Requêtes/jour</span>
                  <span class="font-medium">{{ getRequestsPerDay() }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Commandes/jour</span>
                  <span class="font-medium">{{ getOrdersPerDay() }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Dernière activité</span>
                  <span class="font-medium">{{ formatDateTime(getLastRequestAt()) || '-' }}</span>
                </div>
              </div>
            </div>

            <!-- Webhook Config -->
            <div class="border rounded-lg p-4">
              <h3 class="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <i class="material-icons text-sm mr-2">webhook</i>
                Configuration Webhook
              </h3>
              <div class="space-y-3">
                <div>
                  <span class="text-gray-500 block mb-1">URL Webhook</span>
                  <span class="font-medium text-sm break-all">{{ getWebhookUrl() || 'Non configuré' }}</span>
                </div>
                <div>
                  <span class="text-gray-500 block mb-1">Événements abonnés</span>
                  <div class="flex flex-wrap gap-1 mt-1">
                    <span
                      v-for="event in getWebhookEvents()"
                      :key="event"
                      class="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded"
                    >
                      {{ formatEventName(event) }}
                    </span>
                    <span v-if="!getWebhookEvents().length" class="text-gray-400 text-sm">
                      Aucun événement
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- API Credentials Section -->
          <div class="border rounded-lg p-4">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 flex items-center">
              <i class="material-icons text-sm mr-2">vpn_key</i>
              Clés API
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm text-gray-500 mb-1">API Key</label>
                <div class="flex">
                  <input
                    type="text"
                    :value="getApiKey() || 'Non généré'"
                    readonly
                    class="flex-1 px-3 py-2 border rounded-l bg-gray-50 text-sm"
                  />
                  <button
                    @click="copyToClipboard(getApiKey())"
                    class="px-3 py-2 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200"
                  >
                    <i class="material-icons text-sm">content_copy</i>
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm text-gray-500 mb-1">API Secret</label>
                <div class="flex">
                  <input
                    :type="showSecret ? 'text' : 'password'"
                    :value="getApiSecret() || 'Non généré'"
                    readonly
                    class="flex-1 px-3 py-2 border rounded-l bg-gray-50 text-sm"
                  />
                  <button
                    @click="showSecret = !showSecret"
                    class="px-3 py-2 border border-l-0 bg-gray-100 hover:bg-gray-200"
                  >
                    <i class="material-icons text-sm">{{ showSecret ? 'visibility_off' : 'visibility' }}</i>
                  </button>
                  <button
                    @click="copyToClipboard(getApiSecret())"
                    class="px-3 py-2 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200"
                  >
                    <i class="material-icons text-sm">content_copy</i>
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm text-gray-500 mb-1">API Ref</label>
                <div class="flex">
                  <input
                    type="text"
                    :value="getApiRef() || 'Non généré'"
                    readonly
                    class="flex-1 px-3 py-2 border rounded-l bg-gray-50 text-sm"
                  />
                  <button
                    @click="copyToClipboard(getApiRef())"
                    class="px-3 py-2 border border-l-0 rounded-r bg-gray-100 hover:bg-gray-200"
                  >
                    <i class="material-icons text-sm">content_copy</i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- IP Whitelist -->
          <div v-if="getAllowedIPs().length" class="border rounded-lg p-4">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 flex items-center">
              <i class="material-icons text-sm mr-2">security</i>
              Liste blanche IP
            </h3>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="ip in getAllowedIPs()"
                :key="ip"
                class="px-3 py-1 bg-gray-100 text-gray-700 rounded font-mono text-sm"
              >
                {{ ip }}
              </span>
            </div>
          </div>

          <!-- Fees/Pricing -->
          <div v-if="partner.fees && partner.fees.length" class="border rounded-lg p-4">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 flex items-center">
              <i class="material-icons text-sm mr-2">attach_money</i>
              Tarification B2B
            </h3>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500">Entrepôt</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500">Confirmation</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500">Fulfillment</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500">Shipping</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500">Refund</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500">Commission %</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr v-for="fee in partner.fees" :key="fee.warehouse">
                    <td class="px-4 py-2 text-sm">{{ fee.warehouse }}</td>
                    <td class="px-4 py-2 text-sm">{{ fee.confirmation === -1 ? 'Par défaut' : fee.confirmation }}</td>
                    <td class="px-4 py-2 text-sm">{{ fee.fulfillement_fees === -1 ? 'Par défaut' : fee.fulfillement_fees }}</td>
                    <td class="px-4 py-2 text-sm">{{ fee.shipping === -1 ? 'Par défaut' : fee.shipping }}</td>
                    <td class="px-4 py-2 text-sm">{{ fee.refund === -1 ? 'Par défaut' : fee.refund }}</td>
                    <td class="px-4 py-2 text-sm">{{ fee.commission_percentage === -1 ? 'Par défaut' : fee.commission_percentage + '%' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object }
  },
  data() {
    return {
      loading: false,
      partner: null,
      showSecret: false
    };
  },
  async mounted() {
    await this.fetchPartner();
  },
  methods: {
    async fetchPartner() {
      this.loading = true;
      try {
        const response = await this.$server.get('partners', { partnerId: this.$route.params.id });
        if (response && response.content) {
          this.partner = response.content;
        }
      } catch (error) {
        console.error('Error fetching partner:', error);
        this.$toast.error('Erreur lors du chargement du partenaire');
      } finally {
        this.loading = false;
      }
    },
    // Helper methods for safe property access
    getCompanyName() {
      return this.partner && this.partner.partnerConfig ? this.partner.partnerConfig.companyName : null;
    },
    getContractType() {
      return this.partner && this.partner.partnerConfig && this.partner.partnerConfig.contractType
        ? this.partner.partnerConfig.contractType
        : 'standard';
    },
    getContractStartDate() {
      return this.partner && this.partner.partnerConfig ? this.partner.partnerConfig.contractStartDate : null;
    },
    getContractEndDate() {
      return this.partner && this.partner.partnerConfig ? this.partner.partnerConfig.contractEndDate : null;
    },
    getIsActive() {
      return this.partner && this.partner.partnerConfig ? this.partner.partnerConfig.isActive : false;
    },
    getTotalOrders() {
      return this.partner && this.partner.partnerConfig && this.partner.partnerConfig.apiStats
        ? this.partner.partnerConfig.apiStats.totalOrders || 0
        : 0;
    },
    getTotalRequests() {
      return this.partner && this.partner.partnerConfig && this.partner.partnerConfig.apiStats
        ? this.partner.partnerConfig.apiStats.totalRequests || 0
        : 0;
    },
    getLastRequestAt() {
      return this.partner && this.partner.partnerConfig && this.partner.partnerConfig.apiStats
        ? this.partner.partnerConfig.apiStats.lastRequestAt
        : null;
    },
    getRequestsPerMinute() {
      return this.partner && this.partner.partnerConfig && this.partner.partnerConfig.rateLimit
        ? this.partner.partnerConfig.rateLimit.requestsPerMinute || 60
        : 60;
    },
    getRequestsPerDay() {
      return this.partner && this.partner.partnerConfig && this.partner.partnerConfig.rateLimit
        ? this.partner.partnerConfig.rateLimit.requestsPerDay || 10000
        : 10000;
    },
    getOrdersPerDay() {
      return this.partner && this.partner.partnerConfig && this.partner.partnerConfig.rateLimit
        ? this.partner.partnerConfig.rateLimit.ordersPerDay || 1000
        : 1000;
    },
    getWebhookUrl() {
      return this.partner && this.partner.partnerConfig ? this.partner.partnerConfig.webhookUrl : null;
    },
    getWebhookEvents() {
      return this.partner && this.partner.partnerConfig && this.partner.partnerConfig.webhookEvents
        ? this.partner.partnerConfig.webhookEvents
        : [];
    },
    getAllowedIPs() {
      return this.partner && this.partner.partnerConfig && this.partner.partnerConfig.allowedIPs
        ? this.partner.partnerConfig.allowedIPs
        : [];
    },
    getApiKey() {
      return this.partner && this.partner.api ? this.partner.api.key : null;
    },
    getApiSecret() {
      return this.partner && this.partner.api ? this.partner.api.secret : null;
    },
    getApiRef() {
      return this.partner && this.partner.api ? this.partner.api.ref : null;
    },
    getStatusColor(status) {
      const colors = {
        active: 'text-green-700',
        inactive: 'text-yellow-700',
        blocked: 'text-red-700'
      };
      return colors[status] || 'text-gray-700';
    },
    formatDate(date) {
      if (!date) return null;
      return new Date(date).toLocaleDateString('fr-FR');
    },
    formatDateTime(date) {
      if (!date) return null;
      return new Date(date).toLocaleString('fr-FR');
    },
    formatEventName(event) {
      const names = {
        'order.created': 'Commande créée',
        'order.status_changed': 'Statut commande',
        'shipping.created': 'Expédition créée',
        'shipping.status_changed': 'Statut expédition',
        'shipping.delivered': 'Livré',
        'shipping.returned': 'Retourné'
      };
      return names[event] || event;
    },
    copyToClipboard(text) {
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.success('Copié dans le presse-papiers');
      }).catch(() => {
        this.$toast.error('Erreur lors de la copie');
      });
    }
  }
};
</script>
